import { useMemo } from "react";

import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";

import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { FormStepContainer } from "../FormStepContainer";

import { trpc } from "@/common/helpers/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";

type TicketingFormOption = {
  id: string;
  title: string;
  label: string;
  value: string;
};

export type StepP2PSettingsFormData = {
  createTeams: boolean;
  joinTeams: boolean;
  fundraiseSolo: boolean;
  createAndJoinTeams: boolean;
  addRegistrationForm: boolean;
  selectedTicketingForm: TicketingFormOption | null;
  hideFundraiseButton: boolean;
};

export const LiveFormEditorP2PSettingsStep = () => {
  const { t } = useTranslate();
  const { apiFormObject } = useLiveFormEditorContext<"donation-form">();

  const { data: p2PSettingsStepData, isLoading: isLoadingP2PSettingsStepData } =
    trpc.getLiveFormP2PSettingsStepData.useQuery({
      campaignId: apiFormObject.id,
    });

  const oneTeamHasBeenCreated = p2PSettingsStepData?.oneTeamHasBeenCreated ?? false;

  // Split the toggles whenever allowTeamCreation and allowJoinTeam drift apart (e.g. inherited from a duplicated campaign) — the combined AND-toggle would otherwise silently mask the divergence as OFF.
  const teamFlagsAreOutOfSync = (apiFormObject.allowTeamCreation ?? false) !== (apiFormObject.allowJoinTeam ?? false);
  const showSeparateTeamToggles = oneTeamHasBeenCreated || teamFlagsAreOutOfSync;

  const { data: ticketingForms, isLoading } = trpc.getTicketingFormsForCampaignRegistration.useQuery();

  const ticketingFormOptions = useMemo(
    (): TicketingFormOption[] =>
      ticketingForms?.map((ticketingForm) => {
        const titleField = ticketingForm.ticketingFields?.find((field) => field.locale === apiFormObject.locale);
        const title = titleField?.title || `Ticketing Form ${ticketingForm.id}`;
        return {
          label: title,
          value: ticketingForm.id,
          id: ticketingForm.id,
          title,
        };
      }) || [],
    [ticketingForms, apiFormObject.locale]
  );

  const initialSelectedTicketingForm = useMemo(() => {
    if (apiFormObject.ticketingId && ticketingFormOptions.length > 0) {
      return ticketingFormOptions.find((option) => option.id === apiFormObject.ticketingId) || null;
    }
    return null;
  }, [apiFormObject.ticketingId, ticketingFormOptions]);

  const form = useForm<StepP2PSettingsFormData>({
    values: {
      createTeams: apiFormObject.allowTeamCreation ?? false,
      joinTeams: apiFormObject.allowJoinTeam ?? false,
      fundraiseSolo: apiFormObject.allowFundraiserCreation ?? false,
      createAndJoinTeams: (apiFormObject.allowTeamCreation && apiFormObject.allowJoinTeam) ?? false,
      addRegistrationForm: Boolean(apiFormObject.ticketingId),
      selectedTicketingForm: initialSelectedTicketingForm,
      hideFundraiseButton: apiFormObject.ticketingId ? apiFormObject.hideFundraiseButton : true,
    },
  });

  const selectedTicketingForm = form.watch("selectedTicketingForm");

  const { mutateAsync: updateP2PSettings } = trpc.updateP2PSettings.useMutation();

  const handleTicketingFormChange = (newValue: TicketingFormOption | null | undefined) => {
    form.setValue("selectedTicketingForm", newValue ?? null, { shouldDirty: true });
  };

  const addRegistrationForm = form.watch("addRegistrationForm");

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepP2PSettingsFormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      // Save P2P settings and ticketing form in one call
      const ticketingId =
        formData.addRegistrationForm && formData.selectedTicketingForm ? formData.selectedTicketingForm.id : null;
      const hasSelectedTicketingForm = Boolean(ticketingId);

      return showSeparateTeamToggles
        ? await updateP2PSettings({
            donationFormId: apiFormObject.id,
            allowFundraiserCreation: formData.fundraiseSolo,
            allowTeamCreation: formData.createTeams,
            allowJoinTeam: formData.joinTeams,
            ticketingId,
            hideFundraiseButton: hasSelectedTicketingForm ? formData.hideFundraiseButton : false,
          })
        : await updateP2PSettings({
            donationFormId: apiFormObject.id,
            allowFundraiserCreation: formData.fundraiseSolo,
            allowTeamCreation: formData.createAndJoinTeams,
            allowJoinTeam: formData.createAndJoinTeams,
            ticketingId,
            hideFundraiseButton: hasSelectedTicketingForm ? formData.hideFundraiseButton : false,
          });
    },
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      if (showSeparateTeamToggles) {
        liveDataToMutate.donation.allowTeamCreation = formData.createTeams;
        liveDataToMutate.donation.allowJoinTeam = formData.joinTeams;
        liveDataToMutate.donation.allowFundraiserCreation = formData.fundraiseSolo;
      } else {
        liveDataToMutate.donation.allowTeamCreation = formData.createAndJoinTeams;
        liveDataToMutate.donation.allowJoinTeam = formData.createAndJoinTeams;
        liveDataToMutate.donation.allowFundraiserCreation = formData.fundraiseSolo;
      }

      // Update ticketing form in preview
      const ticketingId =
        formData.addRegistrationForm && formData.selectedTicketingForm ? formData.selectedTicketingForm.id : null;
      if (ticketingId) {
        liveDataToMutate.donation.ticketingId = ticketingId;
        liveDataToMutate.donation.hideFundraiseButton = formData.hideFundraiseButton;
      } else {
        liveDataToMutate.donation.ticketingId = null;
        liveDataToMutate.donation.hideFundraiseButton = false;
      }
    },
  });

  // This API call is so fast that adding a loading state make it more glitchy than useful
  if (isLoadingP2PSettingsStepData) {
    return null;
  }

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepP2PSettings.title")}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
      }}
      form={form}
    >
      <Stack gap={2}>
        {showSeparateTeamToggles ? (
          <>
            <LiveEditorBlock>
              <LiveEditorToggle
                label={t("dashboard", "liveFormEditor.stepP2PSettings.createTeams.label")}
                control={form.control}
                name="createTeams"
              />
            </LiveEditorBlock>

            <LiveEditorBlock>
              <LiveEditorToggle
                label={t("dashboard", "liveFormEditor.stepP2PSettings.joinTeams.label")}
                control={form.control}
                name="joinTeams"
              />
            </LiveEditorBlock>
          </>
        ) : (
          <LiveEditorBlock>
            <LiveEditorToggle
              label={t("dashboard", "liveFormEditor.stepP2PSettings.createAndJoinTeams.label")}
              control={form.control}
              name="createAndJoinTeams"
            />
          </LiveEditorBlock>
        )}

        <LiveEditorBlock>
          <LiveEditorToggle
            label={t("dashboard", "liveFormEditor.stepP2PSettings.fundraiseSolo.label")}
            control={form.control}
            name="fundraiseSolo"
          />
        </LiveEditorBlock>

        <LiveEditorBlock>
          <LiveEditorToggle
            label={t("dashboard", "liveFormEditor.stepP2PSettings.addRegistrationForm.label")}
            control={form.control}
            name="addRegistrationForm"
            tooltip={t("dashboard", "liveFormEditor.stepP2PSettings.addRegistrationForm.tooltip")}
          />
          {form.watch("addRegistrationForm") && (
            <Box marginTop={2}>
              <DashboardAutocomplete
                control={form.control}
                name="selectedTicketingForm"
                options={ticketingFormOptions}
                value={selectedTicketingForm}
                onChange={handleTicketingFormChange}
                loading={isLoading}
                placeholder={t("dashboard", "liveFormEditor.stepCampaignRegistration.selectTicketingForm.placeholder")}
                label={t("dashboard", "liveFormEditor.stepCampaignRegistration.selectTicketingForm.label")}
                data-test="ticketing-form-selector"
                disableClearable={false}
                freeSolo={false}
                noOptionsText={t("dashboard", "liveFormEditor.stepCampaignRegistration.noTicketingForms")}
                rules={
                  addRegistrationForm
                    ? [
                        {
                          validate: {
                            required: (value: TicketingFormOption | null) => {
                              return value
                                ? true
                                : t(
                                    "dashboard",
                                    "liveFormEditor.stepCampaignRegistration.selectTicketingForm.required"
                                  );
                            },
                          },
                        },
                      ]
                    : undefined
                }
                errorMessage={form.formState.errors.selectedTicketingForm?.message}
              />
              {form.watch("selectedTicketingForm") && (
                <Box marginTop={2}>
                  <DashboardCheckbox
                    name="hideFundraiseButton"
                    label={t("dashboard", "liveFormEditor.stepP2PSettings.isMandatory.label")}
                    control={form.control}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          )}
        </LiveEditorBlock>
      </Stack>
    </FormStepContainer>
  );
};
